// #region Imports
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import WorkIcon from "@mui/icons-material/Work";
import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  type MRT_TableInstance,
  type MRT_SortingState as SortingState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_Updater,
} from "material-react-table";
import { ToolbarButtonsContainer } from "_shared/_views/tables/toolbar-buttons";
import {
  MobileToolbarButtonsContainer,
  searchFieldSpacing,
  ScrollableTabs,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import ChatDialog from "messaging/forums/_views/components/forum-chat-dialog";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLoaderData, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import UserInformation from "users/information/user-information";
import { PageTab } from "users/networking/_views/network-page-tabs";
import NetworkConnectionQuery, {
  NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH,
  NetworkConnectionOrderField,
} from "users/networking/connections/_api/queries/network-connection-query";
import {
  useCreateChatForumForNetworkConnectionMutation,
  useGetChatForumForNetworkConnectionQuery,
  useGetUserNetworkConnectionsQuery,
  useRemoveNetworkConnectionMutation,
} from "users/networking/connections/_api/user-network-connection-api";
import startDraftProposalWithVendorTeamLeader from "work/proposals/_views/drafts/start-draft-proposal-with-vendor-team-leader";
import { DateCell } from "_shared/_views/tables/date-cells";
import { formatDate } from "_shared/formatting/dates";
// #endregion

// #region Styled Components
const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  minWidth: 0,
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const NameCellContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));
// #endregion

const columnHelper = createMRTColumnHelper<UserInformation>();

/** The sortable columns, by the ids the sort state carries. */
export const NAME_COLUMN = "name";
export const DATE_CONNECTED_COLUMN = "createdDate";

/** Where a contact's profile opens: the row, the name and the ⋯'s Open all go here. */
export function contactDestination(contact: UserInformation): string {
  return `/network/contacts/view/${contact.individualId}`;
}

/** Sorted by name, ascending: the server's own default, so the header arrow says what the rows are in. */
export const DEFAULT_SORTING: SortingState = [{ id: NAME_COLUMN, desc: false }];

/**
 * The order the server is asked for, from the order the table shows.
 *
 * Always one column and always a direction. The server orders every page, so an "unsorted" state
 * would be a header arrow that means nothing. `enableSortingRemoval` is off for the same reason.
 */
export function networkConnectionOrderFrom(sorting: SortingState): {
  orderBy: NetworkConnectionOrderField;
  orderDir: OrderDirection;
} {
  const first = sorting.length > 0 ? sorting[0] : undefined;
  return {
    orderBy: first && first.id === DATE_CONNECTED_COLUMN
      ? NetworkConnectionOrderField.DateConnected
      : NetworkConnectionOrderField.Name,
    orderDir: first && first.desc ? OrderDirection.Descending : OrderDirection.Ascending,
  };
}

/** Whether the server accepts the search. It refuses a search longer than it can match. */
export function isContactsGlobalFilterValid(value: string): boolean {
  return value.trim().length <= NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH;
}

/** MRT hands state changes as either a value or a function of the old one. */
function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

export default function ContactsTab() {
  const handledRef = React.useRef<string | null>(null);

  // #region State
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [sorting, setSorting] = React.useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  // #Hooks
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Route
  const routeParams = useLoaderData();
  const routeAction = routeParams?.action as string | undefined;
  const routeId = routeParams?.id as string | undefined;
  const chatUserId = useMemo(
    () => (routeAction === "message" && routeId ? new Guid(routeId) : undefined),
    [routeAction, routeId],
  );

  // #region Queries
  const isGlobalFilterValid = isContactsGlobalFilterValid(globalFilter);

  // No debounce here. MRT's own `GlobalFilterTextField` debounces the typed text and pushes it into
  // `globalFilter` after 500ms while `manualFiltering` is on. A second debounce would make the
  // reader wait twice.
  const query = useMemo(() => {
    if (!isGlobalFilterValid) return undefined;

    const order = networkConnectionOrderFrom(sorting);
    return new NetworkConnectionQuery({
      paginationParameters: PaginationParameters.create(pagination),
      orderBy: order.orderBy,
      orderDir: order.orderDir,
      query: globalFilter,
    });
  }, [globalFilter, isGlobalFilterValid, pagination, sorting]);

  const networkConnectionQuery = useGetUserNetworkConnectionsQuery(query ? { query } : skipToken);
  const [removeConnection, removeConnectionResult] = useRemoveNetworkConnectionMutation();
  const [createChat, createChatResult] = useCreateChatForumForNetworkConnectionMutation();
  const chatQuery = useGetChatForumForNetworkConnectionQuery(chatUserId ? { userId: chatUserId } : skipToken);

  const networkConnections = useMemo(() => {
    if (!networkConnectionQuery.data) return PaginatedResponse.empty<UserInformation>();
    return PaginatedResponse.from<UserInformation>(
      UserInformation.fromJSONArray(networkConnectionQuery.data.data),
      networkConnectionQuery.data.headers,
    );
  }, [networkConnectionQuery.data]);
  const isFetchingConnections = networkConnectionQuery.isFetching;

  const forum = useMemo(() => {
    if (!chatQuery.data) return null;
    return Forum.fromJSON(chatQuery.data);
  }, [chatQuery.data]);

  const handleSendMessageToContact = useCallback(
    async (userId?: Guid): Promise<void> => {
      try {
        if (!userId) throw new Error("No user id provided");

        const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

        let activeForum = forum;

        if (!forum) {
          await createChat({ connectionId: userId });
          if (!createChatResult.data) {
            throw new Error("Failed to create chat forum");
          }
          activeForum = Forum.fromJSON(createChatResult.data);
        }

        if (!activeForum) {
          throw new Error("No active forum available");
        }

        openDialog({
          title: "Message",
          contentSxProps: {
            display: "flex",
            overflowX: "hidden",
            paddingBottom: 0,
          },
          MuiProps: {
            fullWidth: true,
            maxWidth: "md",
          },
          component: <ChatDialog forums={[activeForum]} />,
          onClose: () => {
            navigate(parentPath.pathname);
          },
        });
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Chat currently unavailable", { variant: "error" });
      }
    },
    [forum, createChat, createChatResult.data, openDialog, navigate],
  );

  /**
   * Opens the profile of one contact.
   *
   * A contact on the loaded page opens from its row. A contact that is not on it (the route names
   * somebody on another page, or outside the current search) opens by individual id alone: the
   * profile then loads the individual itself, and takes the proposal target from it.
   */
  const handleViewProfile = useCallback(
    (individualId: Guid, data?: UserInformation): void => {
      const individualProfile = data
        ? new IndividualProfile({
            userId: data.id,
            firstName: data.name.firstName ?? "",
            lastName: data.name.lastName ?? "",
            avatarId: data.avatarId,
            legalEntityId: data.legalEntityId,
            email: data.email,
          })
        : undefined;
      const proposalTarget = data
        ? {
            userId: data.id,
            legalEntityId: data.legalEntityId,
            hasSigningAuthority: data.hasSigningAuthority,
          }
        : undefined;

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        onClose: () => {
          const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);
          navigate(parentPath.pathname);
        },
        component: (
          <ViewIndividualProfile
            individualId={individualId}
            profile={individualProfile}
            proposalTarget={proposalTarget}
          />
        ),
      });
    },
    [openDialog, navigate],
  );

  // #region Lifecycle
  useEffect(() => {
    // Handle URL changes. A route that names nobody is the dialog having closed, so forget what it
    // opened: otherwise clicking the same contact again finds its key handled and opens nothing.
    if (!routeId) {
      handledRef.current = null;
      return;
    }

    const key = `${routeAction}-${routeId}`;
    if (handledRef.current === key) return;

    if (routeAction === "message" && chatUserId) {
      handledRef.current = key;
      void handleSendMessageToContact(chatUserId);
    } else if (routeAction === "view") {
      // Wait for the page in flight, so that a contact on it opens from its row.
      if (isFetchingConnections) return;
      const match = networkConnections.data.find((nc) => nc.individualId?.value === routeId);
      handledRef.current = key;
      handleViewProfile(new Guid(routeId), match);
    }
  }, [
    routeAction,
    routeId,
    networkConnections.data,
    isFetchingConnections,
    handleSendMessageToContact,
    handleViewProfile,
    chatUserId,
  ]);
  // #endregion

  // #region Functions
  const handleRemoveNetworkConnection = useCallback(
    async (connectionId?: Guid): Promise<void> => {
      try {
        if (!connectionId) throw new Error("No network connection id provided");

        const response = await confirm({
          title: "Remove Network Connection",
          message: "Are you sure you want to remove this network connection?",
          okButtonText: "Remove",
        });

        if (response === ConfirmResponse.Cancel) return;

        await removeConnection({ connectionId });

        if (removeConnectionResult.isError) {
          throw removeConnectionResult.error;
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Failed to remove network connection", {
          variant: "error",
        });
      }
    },
    [confirm, removeConnection, removeConnectionResult.isError, removeConnectionResult.error],
  );

  const canCreateProposalFromContact = useCallback((contact: UserInformation): boolean => {
    if (session.user?.id?.isEqualTo(contact.id)) return false;
    return session.user?.legalEntityId?.isEqualTo(contact.legalEntityId) === false;
  }, [session.user?.id, session.user?.legalEntityId]);

  const handleCreateProposalFromContact = useCallback((contact: UserInformation): void => {
    if (!canCreateProposalFromContact(contact)) return;

    startDraftProposalWithVendorTeamLeader({
      dispatch,
      leader: {
        userId: contact.id,
        legalEntityId: contact.legalEntityId,
      },
      navigate,
      session,
    });
  }, [canCreateProposalFromContact, dispatch, navigate, session]);
  // #endregion

  // #region Columns
  const rowActions = useCallback(
    (row: Row<UserInformation>) => {
      const contactName = row.original.name.fullName;
      const canCreateProposal = canCreateProposalFromContact(row.original);
      return (
        <RowActions
          ariaLabel={`Actions for ${contactName}`}
          open={{ to: contactDestination(row.original) }}
          actions={[
            {
              key: "create-proposal",
              label: "Create Proposal",
              ariaLabel: `Create proposal from ${contactName}`,
              icon: <WorkIcon />,
              disabled: !canCreateProposal,
              disabledReason: "Proposals can only be created with contacts outside your organization",
              onClick: () => handleCreateProposalFromContact(row.original),
            },
            {
              key: "remove-connection",
              label: "Remove Connection",
              ariaLabel: `Remove connection with ${contactName}`,
              icon: <DeleteIcon />,
              destructive: true,
              onClick: () => {
                void handleRemoveNetworkConnection(row.original.connectionId);
              },
            },
          ]}
        />
      );
    },
    [canCreateProposalFromContact, handleCreateProposalFromContact, handleRemoveNetworkConnection],
  );

  // The company reads under the name, rather than in a column of its own (#6722).
  const renderContactNameColumn = useCallback(
    (user: UserInformation, table: MRT_TableInstance<UserInformation>) => {
      return (
        <NameCellContainer>
          <IndividualAvatar individual={user.toIndividual()} />
          <CondensedCell
            title={
              <RowLink to={contactDestination(user)}>
                <SearchMatch text={user.name.fullName} table={table} />
              </RowLink>
            }
            secondary={user.companyName && <SearchMatch text={user.companyName} table={table} />}
          />
        </NameCellContainer>
      );
    },
    [],
  );

  const columns = useMemo(
    () => [
      // Accessor columns, so that MRT shows a sort arrow. The server sorts: the name by last name,
      // then first name, and the date by when the connection was made.
      columnHelper.accessor((row) => row.name.fullName, {
        header: "Name",
        id: NAME_COLUMN,
        enableSorting: true,
        meta: { kind: "name" },
        Cell: (info) => renderContactNameColumn(info.row.original, info.table),
      }),
      columnHelper.accessor((row) => formatDate(row.createdDate) ?? "", {
        header: "Connected",
        id: DATE_CONNECTED_COLUMN,
        enableSorting: true,
        // The search matches names, emails and companies, never a date: no mark here (#6590).
        enableGlobalFilter: false,
        meta: { kind: "date" },
        Cell: (info) => <DateCell value={info.row.original.createdDate} />,
      }),
    ],
    [renderContactNameColumn],
  );
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<UserInformation> = {
    columns: columns,
    data: networkConnections.data,
    getRowId: (row) => row.connectionId?.value ?? row.id?.value ?? "",
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => rowActions(row),
    renderToolbarInternalActions: ({ table }) =>
      !isMediumDisplaySize && (
        <ToolbarButtonsContainer>
          <ToolbarButtons>
            <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
            <TableToolsMenu table={table} />
          </ToolbarButtons>
        </ToolbarButtonsContainer>
      ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Network</TableTitle>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={PageTab.Contacts}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue) => {
                navigate(`/network/${newValue}`);
              }}
            >
              <Tab value={PageTab.Contacts} label="Contacts" />
              <Tab value={PageTab.Coworkers} label="Coworkers" />
              <Tab value={PageTab.Pending} label="Pending" />
              <Tab value={PageTab.Groups} label="Groups" />
            </ScrollableTabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <TableToolsMenu table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({
        searches: "contacts",
        table,
        maxLength: NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH,
      }),
      error: !isGlobalFilterValid,
      helperText: !isGlobalFilterValid
        ? `Search must be ${NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH} characters or fewer.`
        : undefined,
    }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: networkConnectionQuery.isError,
      isLoading: networkConnectionQuery.isFetching,
      onRetry: () => void networkConnectionQuery.refetch(),
      subject: "contacts",
    }),
    // The server pages, searches and sorts. No column filters: the search box does that work.
    enableColumnFilters: false,
    enableMultiSort: false,
    // The server orders every page, so there is no unsorted state for a third click to mean.
    enableSortingRemoval: false,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate(contactDestination(row.original));
      },
      sx: { cursor: "pointer" },
    }),
    muiTableContainerProps: {
      sx: {
        flex: 1,
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    muiToolbarAlertBannerProps: networkConnectionQuery.isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    rowCount: networkConnections.totalElements,
    // A new search or a new order is a new result set, so the reader goes back to its first page.
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setGlobalFilter(applyUpdater(updater, globalFilter) ?? "");
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onPaginationChange: (updater: MRT_Updater<PaginationState>) => {
      const next = applyUpdater(updater, pagination);
      // A different page size is a different set of pages, so the reader goes back to the first.
      setPagination(next.pageSize === pagination.pageSize ? next : { ...next, pageIndex: 0 });
    },
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    state: {
      globalFilter,
      isLoading: networkConnectionQuery.isLoading,
      pagination,
      showAlertBanner: networkConnectionQuery.isError,
      showProgressBars: networkConnectionQuery.isFetching,
      showSkeletons: networkConnectionQuery.isLoading,
      sorting,
    },
  }
  // #endregion

  // #region Render
  return <MRTNoMemo<UserInformation> options={tableOptions} cards={{ defaultSorting: DEFAULT_SORTING }} />;
}
